import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CalendarClock,
  Hourglass,
  PackageX,
  ShieldAlert,
  Snowflake,
  TrendingDown,
} from 'lucide-react';
import type { StockAlerts, StockLevel } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { useBranch } from '@/hooks/useBranch';
import { apiErrorMessage } from '@/services/apiClient';
import { useDeadStockQuery, useSlowMovingQuery, useStockAlertsQuery } from '../api/inventory.api';
import { StagnantStockPanel } from '../components/StagnantStockPanel';
import { ExpiryBadge } from '../components/StockStateBadge';

type ExpiringBatch = StockAlerts['expiringSoon'][number];

function StockRows({ levels, emptyLabel }: { levels: StockLevel[]; emptyLabel: string }) {
  const { t } = useTranslation();

  if (levels.length === 0) {
    return <EmptyState title={emptyLabel} className="px-5 py-8" />;
  }

  return (
    <ul className="divide-line divide-y">
      {levels.map((level) => (
        <li key={level.id} className="flex items-center gap-3 px-5 py-3">
          <span className="min-w-0 flex-1">
            <span className="text-ink block truncate text-sm font-medium">{level.productName}</span>
            <span className="text-muted block text-xs">
              <span className="font-mono" dir="ltr">
                {level.sku}
              </span>
              {level.variantName ? <span className="mx-1.5">· {level.variantName}</span> : null}
            </span>
          </span>

          <span className="shrink-0 text-end">
            <span className="text-ink block text-sm font-semibold" dir="ltr">
              {level.quantityOnHand}
            </span>
            {level.reorderPoint !== null ? (
              <span className="text-muted block text-xs">
                {t('inventory.fields.reorderPoint')}: <span dir="ltr">{level.reorderPoint}</span>
              </span>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

function BatchRows({ batches, emptyLabel }: { batches: ExpiringBatch[]; emptyLabel: string }) {
  if (batches.length === 0) {
    return <EmptyState title={emptyLabel} className="px-5 py-8" />;
  }

  return (
    <ul className="divide-line divide-y">
      {batches.map((batch) => (
        <li key={batch.id} className="flex items-center gap-3 px-5 py-3">
          <span className="min-w-0 flex-1">
            <span className="text-ink block truncate text-sm font-medium">{batch.productName}</span>
            <span className="text-muted block text-xs">
              <span className="font-mono" dir="ltr">
                {batch.sku} · {batch.batchNumber}
              </span>
              {batch.expiryDate ? (
                <span className="mx-1.5" dir="ltr">
                  · {batch.expiryDate}
                </span>
              ) : null}
            </span>
          </span>

          <span className="shrink-0 text-end">
            <span className="text-ink block text-sm font-semibold" dir="ltr">
              {batch.quantityInBranch ?? 0}
            </span>
            <ExpiryBadge daysUntilExpiry={batch.daysUntilExpiry} />
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * What needs attention at this branch right now.
 *
 * Expired lots are their own section rather than the far end of "expiring soon": dispensing an
 * expired contact lens is a safety problem, not a stock-planning one.
 */
export function StockAlertsPage() {
  const { t } = useTranslation();
  const { activeBranch } = useBranch();
  const { data, isPending, isError, error, refetch } = useStockAlertsQuery();
  // Loaded alongside the alerts rather than folded into them: these answer a different question —
  // what has stopped moving, rather than what is about to run out.
  const slowMoving = useSlowMovingQuery();
  const deadStock = useDeadStockQuery();

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('inventory.alerts.title')}
        subtitle={
          activeBranch
            ? t('inventory.alerts.subtitleBranch', { branch: activeBranch.name })
            : t('inventory.alerts.subtitle')
        }
      />

      {!activeBranch ? (
        <Alert tone="warning">{t('inventory.stock.selectBranch')}</Alert>
      ) : isPending ? (
        <LoadingState />
      ) : isError || !data ? (
        <Alert tone="error">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{apiErrorMessage(error) ?? t('inventory.alerts.loadError')}</span>
            <button type="button" className="underline" onClick={() => void refetch()}>
              {t('common.retry')}
            </button>
          </div>
        </Alert>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <StatCard
              icon={TrendingDown}
              tone="warning"
              label={t('inventory.alerts.lowStock')}
              value={data.counts.lowStock}
            />
            <StatCard
              icon={PackageX}
              tone="danger"
              label={t('inventory.alerts.outOfStock')}
              value={data.counts.outOfStock}
            />
            <StatCard
              icon={CalendarClock}
              tone="warning"
              label={t('inventory.alerts.expiringSoon')}
              value={data.counts.expiringSoon}
            />
            <StatCard
              icon={ShieldAlert}
              tone="danger"
              label={t('inventory.alerts.expired')}
              value={data.counts.expired}
            />
            <StatCard
              icon={Hourglass}
              tone="warning"
              label={t('inventory.alerts.slowMoving')}
              value={slowMoving.data?.lines.length ?? 0}
              isLoading={slowMoving.isPending}
            />
            <StatCard
              icon={Snowflake}
              tone="danger"
              label={t('inventory.alerts.deadStock')}
              value={deadStock.data?.lines.length ?? 0}
              isLoading={deadStock.isPending}
            />
          </div>

          {data.counts.expired > 0 ? (
            <Alert tone="error">{t('inventory.alerts.expiredWarning')}</Alert>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader
                title={t('inventory.alerts.expired')}
                subtitle={t('inventory.alerts.expiredHint')}
              />
              <BatchRows batches={data.expired} emptyLabel={t('inventory.alerts.noExpired')} />
            </Card>

            <Card>
              <CardHeader
                title={t('inventory.alerts.expiringSoon')}
                subtitle={t('inventory.alerts.expiringSoonHint')}
              />
              <BatchRows
                batches={data.expiringSoon}
                emptyLabel={t('inventory.alerts.noExpiringSoon')}
              />
            </Card>

            <Card>
              <CardHeader
                title={t('inventory.alerts.outOfStock')}
                subtitle={t('inventory.alerts.outOfStockHint')}
              />
              <StockRows levels={data.outOfStock} emptyLabel={t('inventory.alerts.noOutOfStock')} />
            </Card>

            <Card>
              <CardHeader
                title={t('inventory.alerts.lowStock')}
                subtitle={t('inventory.alerts.lowStockHint')}
              />
              <StockRows levels={data.lowStock} emptyLabel={t('inventory.alerts.noLowStock')} />
            </Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <StagnantStockPanel
              title={t('inventory.alerts.slowMoving')}
              subtitle={t('inventory.alerts.slowMovingHint', {
                days: slowMoving.data?.thresholdDays ?? 0,
              })}
              emptyTitle={t('inventory.alerts.noSlowMoving')}
              emptyIcon={Hourglass}
              report={slowMoving.data}
              isLoading={slowMoving.isPending}
              isError={slowMoving.isError}
              error={slowMoving.error}
              onRetry={() => void slowMoving.refetch()}
            />

            <StagnantStockPanel
              title={t('inventory.alerts.deadStock')}
              subtitle={t('inventory.alerts.deadStockHint', {
                days: deadStock.data?.thresholdDays ?? 0,
              })}
              emptyTitle={t('inventory.alerts.noDeadStock')}
              emptyIcon={Snowflake}
              report={deadStock.data}
              isLoading={deadStock.isPending}
              isError={deadStock.isError}
              error={deadStock.error}
              onRetry={() => void deadStock.refetch()}
            />
          </div>

          {data.counts.lowStock + data.counts.outOfStock + data.counts.expired === 0 ? (
            <Alert tone="success">
              <span className="inline-flex items-center gap-2">
                <AlertTriangle className="size-4" aria-hidden />
                {t('inventory.alerts.allClear')}
              </span>
            </Alert>
          ) : null}
        </>
      )}
    </div>
  );
}
